/**
 * useLaggingConversations (CHAP-302) - lag list + one-click follow-up.
 *
 * The endpoint is cabinet+ (donor capacity tiers are cabinet-visible per
 * capabilities.ts view_donor_capacity), so the query self-disables below
 * cabinet - the sidebar badge and the fundraising panel share this hook
 * (and the React Query cache entry) without ever firing a 403.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api.js';
import { useUserRoles } from './useUserRoles.js';
import type { LaggingConversationsResponse } from '../../shared/lagging.js';

/** Mirrors TierGuard semantics: cabinet tier, or team/admin bypass. */
export function useIsCabinetPlus(): boolean {
  const { tier, allowedRoles } = useUserRoles();
  return allowedRoles.includes('team') || allowedRoles.includes('admin') || tier === 'cabinet';
}

export function useLaggingConversations(slug: string) {
  const isCabinetPlus = useIsCabinetPlus();
  return useQuery<LaggingConversationsResponse, Error>({
    queryKey: ['lagging-conversations', slug],
    queryFn: () => apiGet<LaggingConversationsResponse>(`/api/c/${slug}/lagging-conversations`),
    enabled: !!slug && isCabinetPlus,
    staleTime: 60_000,
  });
}

export function useLogFollowUp(slug: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean; eventId: string }, Error, { recordId: string; note?: string }>({
    mutationFn: ({ recordId, note }) =>
      apiPost(`/api/c/${slug}/lagging-conversations/${recordId}/follow-up`, note ? { note } : {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lagging-conversations', slug] }),
  });
}
